import React, { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Heart, Shield, Users, Sparkle, HandHeart, MapPin } from "@phosphor-icons/react";

const VALUES = [
  {
    icon: Heart,
    title: "L'enfance d'abord",
    text: "Un doudou n'est pas un objet : c'est un compagnon, un confident, un repère. Quand il disparaît, c'est tout l'équilibre d'un enfant qui vacille. Nous prenons cela au sérieux.",
  },
  {
    icon: Shield,
    title: "Anonymat & sécurité",
    text: "Les prénoms uniquement, les adresses jamais. Vos données sont chiffrées, jamais revendues, et vous gardez le contrôle. Aucune photo d'enfant n'est demandée.",
  },
  {
    icon: HandHeart,
    title: "Entraide gratuite",
    text: "Le cœur du service est et restera 100 % gratuit. On peut tous se retrouver dans l'autre, parent ou pas, à secourir un doudou un jour ou l'autre.",
  },
  {
    icon: Sparkle,
    title: "Simple et doux",
    text: "Pensé pour des parents fatigués (et un peu stressés). Interface claire, mots rassurants, parcours minimal. On n'est pas une appli, on est une main tendue.",
  },
];

function FounderPhoto() {
  // Affiche /founder-carole.jpg si présent, sinon un avatar dégradé en fallback.
  const [failed, setFailed] = useState(false);
  return (
    <div className="w-full sm:w-44 md:w-52 shrink-0" data-testid="founder-photo">
      <div className="aspect-[3/4] rounded-3xl overflow-hidden bg-gradient-to-br from-rose-200 via-orange-200 to-amber-200 shadow-lg border-4 border-white">
        {!failed ? (
          <img
            src="/founder-carole.jpg"
            alt="Carole, fondatrice de Doudou Alert"
            className="w-full h-full object-cover"
            onError={() => setFailed(true)}
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-rose-600">
            <div className="text-center px-4">
              <div className="text-5xl font-display font-black drop-shadow">C</div>
              <p className="text-xs mt-1 font-semibold text-rose-700/80">Photo à venir</p>
            </div>
          </div>
        )}
      </div>
      <p className="text-xs text-stone-500 text-center mt-2 font-semibold uppercase tracking-wider">Carole · Fondatrice</p>
    </div>
  );
}

export default function About() {
  return (
    <Layout>
      <SEO
        title="À propos"
        description="Doudou Alert, un réseau de solidarité bienveillant pour retrouver les doudous perdus. Un projet indépendant porté par une maman, gratuit et anonyme."
      />
      <div className="max-w-4xl mx-auto px-4 py-12" data-testid="about-page">
        {/* Hero */}
        <div className="text-center">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-orange-500">Notre histoire</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-stone-800 mt-3 leading-tight" data-testid="about-title">
            L'union fait la force pour retrouver <span className="text-rose-500">les compagnons préférés</span>
          </h1>
          <div className="text-stone-600 mt-6 max-w-2xl mx-auto leading-relaxed text-lg space-y-4">
            <p>
              Perdre un doudou, ce n'est pas seulement perdre un objet. C'est perdre un <strong>confident</strong>,
              un <strong>rassureur</strong>, le témoin des petits et grands moments de vie de nos enfants. Chez
              Doudou Alert, nous savons que l'urgence est réelle et que chaque minute compte pour apaiser un chagrin.
            </p>
            <p>
              Notre mission est simple : <strong>créer un réseau de solidarité bienveillant</strong> pour transformer
              l'angoisse de la perte en une recherche efficace et sereine.
            </p>
          </div>
        </div>

        {/* Story */}
        <section className="mt-16 bg-white rounded-3xl border border-rose-100 p-8 md:p-10 shadow-sm">
          <h2 className="font-display text-2xl font-bold text-stone-800 text-center">Pourquoi Doudou Alert ?</h2>
          <div className="mt-4 space-y-4 text-stone-700 leading-relaxed">
            <p>
              Il y a tous ces messages dans les groupes Facebook locaux, ces affichettes dans les supermarchés,
              ces SMS désespérés entre parents. Mais jusqu'ici, <strong>aucun service ne réunissait au même endroit</strong>{" "}
              les familles qui ont perdu et les personnes de bonne volonté qui ont trouvé.
            </p>
            <p>
              Alors, nous avons construit Doudou Alert : une plateforme conçue pour transformer cette solidarité
              spontanée en une <strong>action concrète et rapide</strong>.
            </p>

            <ul className="pl-1 space-y-3 mt-2">
              <li className="flex items-start gap-3">
                <span className="mt-1.5 inline-block w-2 h-2 rounded-full bg-rose-500 shrink-0" />
                <span><strong>Une solution tout-en-un</strong> · Chaque famille peut garder une trace de ses doudous et déclarer une perte en quelques secondes.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1.5 inline-block w-2 h-2 rounded-full bg-rose-500 shrink-0" />
                <span><strong>Accessible à tous</strong> · N'importe qui peut signaler une découverte, sans même avoir à créer de compte, pour lever tous les freins à l'entraide.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1.5 inline-block w-2 h-2 rounded-full bg-rose-500 shrink-0" />
                <span><strong>Sécurité et bienveillance</strong> · Surtout, la communication entre les deux parties reste anonyme et sécurisée. Parce que la confiance, dans ces moments-là, fait toute la différence.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Values */}
        <section className="mt-12">
          <h2 className="font-display text-2xl font-bold text-stone-800 text-center mb-8">Nos valeurs</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {VALUES.map((v) => (
              <div key={v.title} className="bg-white rounded-3xl border border-rose-100 p-6 hover:shadow-md transition" data-testid={`value-${v.title}`}>
                <div className="w-12 h-12 rounded-2xl bg-orange-100 text-orange-600 grid place-items-center mb-4">
                  <v.icon size={24} weight="duotone" />
                </div>
                <h3 className="font-display text-lg font-bold text-stone-800">{v.title}</h3>
                <p className="text-stone-600 mt-2 text-sm leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Behind the scenes — Carole */}
        <section className="mt-12 bg-gradient-to-br from-rose-50 to-orange-50 rounded-3xl p-6 sm:p-8 border border-rose-100" data-testid="about-founder">
          <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
            <FounderPhoto />
            <div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-white grid place-items-center shrink-0 shadow-sm">
                  <Users size={26} weight="duotone" className="text-rose-500" />
                </div>
                <h2 className="font-display text-2xl font-bold text-stone-800">Qui se cache derrière Doudou Alert ?</h2>
              </div>
              <div className="text-stone-700 mt-4 leading-relaxed space-y-3">
                <p>
                  Derrière l'écran, il y a moi, <strong>Carole</strong> ! Maman d'une petite fille née en 2022, je
                  connais par cœur l'importance capitale de <em>« Doudou »</em> dans le quotidien.
                </p>
                <p>
                  Professionnellement, j'évolue dans le secteur du <strong>spatial</strong> — un domaine où la
                  précision, la rigueur et la gestion des imprévus sont essentielles. Mais dans la vie de tous les
                  jours, je suis une <strong>bricoleuse passionnée</strong> et une <strong>créative dans l'âme</strong>,
                  toujours en quête de solutions concrètes pour simplifier le quotidien. Qu'il s'agisse de réaliser
                  des projets manuels ou de concevoir de nouvelles idées, j'aime imaginer et construire des solutions
                  qui facilitent la vie.
                </p>
                <p>
                  Doudou Alert, c'est la rencontre de mon goût pour l'innovation et de mon intuition de maman. Avec
                  ce projet, j'ai voulu mettre mon sens du concret et ma créativité au service d'une cause qui me
                  tient à cœur : <strong>le bien-être de nos enfants et la tranquillité d'esprit des parents</strong>.
                </p>
                <p className="font-display text-rose-600 font-bold">
                  Bienvenue dans cette belle aventure de solidarité !
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Future / roadmap */}
        <section className="mt-12">
          <h2 className="font-display text-2xl font-bold text-stone-800 text-center mb-2">Ce qui arrive</h2>
          <p className="text-stone-500 text-center text-sm mb-8">La feuille de route, en toute transparence</p>
          <div className="space-y-3">
            <div className="bg-white rounded-2xl border border-emerald-200 p-5 flex items-center gap-4">
              <span className="text-2xl">✅</span>
              <div>
                <p className="font-display font-bold text-stone-800">Lancement de la plateforme gratuite</p>
                <p className="text-sm text-stone-500">Fiches doudou, carte interactive, chat anonyme, alertes par département</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-rose-100 p-5 flex items-center gap-4">
              <span className="text-2xl">🌱</span>
              <div>
                <p className="font-display font-bold text-stone-800">Communauté & application mobile</p>
                <p className="text-sm text-stone-500">Notifications push, partage simplifié sur les réseaux locaux</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-12">
          <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-orange-500 to-rose-500 p-10 text-white text-center shadow-xl shadow-orange-300/30">
            <MapPin size={36} weight="duotone" className="mx-auto opacity-80" />
            <h2 className="font-display text-3xl font-black mt-3">Rejoignez l'aventure</h2>
            <p className="mt-3 text-white/90 max-w-xl mx-auto">Quelques minutes pour créer la fiche de votre doudou. Une tranquillité pour des années.</p>
            <Link to="/inscription" data-testid="about-cta-signup">
              <Button className="mt-6 bg-white text-orange-600 hover:bg-stone-50 font-bold rounded-full px-8 py-6 text-base shadow-xl">
                Créer mon compte gratuit
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
}
